//
// # Schematic Content
//

// Local Imports
import {
  logger,
  exhaust,
  Result,
  AsyncResult,
  Context,
  paramValueToString,
} from "CoreUtils";
import { model, schdata, randomId, graphics, Point } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import { canPasteSymbolIntoPdk } from "Depot";
import { RedrawRequest } from "DrawCore/src/redraw";
import type { InteractionHost } from "../../interaction_host";
import * as modes from "../../modes";
import * as changes from "DrawCore/src/changes";
import { Keys, hasPlatformCmdModifier } from "../../keys";
import { MousePos } from "DrawCore/src/mousepos";
import { EntityKind } from "DrawCore/src/entity";
import { Annotation, Label } from "DrawCore/src/content/shared";
import { Annotations } from "../shared/annotations";
import { WireSegment } from "DrawCore/src/content/schematic/wire";
import { Instance, SchPin } from "DrawCore/src/content/schematic/instance";
import { Data as ResolvedInstanceData } from "DrawCore/src/content/schematic/instance/resolved";
import { Dot } from "DrawCore/src/content/schematic/dot";
import { Dots } from "DrawCore/src/content/schematic/dots";
import { Wires } from "DrawCore/src/content/schematic/wires";
import { schematicClipboard } from "./clipboard";
import { ContentKind } from "DrawCore/src/content/kind";
import { InteractionBase } from "../interaction_base";
import { SchematicRender } from "DrawCore/src/content/schematic/render";
import type { FieldEdits } from "../fieldEdits";
import { AddInstance, AddSchPin, DrawWire } from "./modes";
import {
  DrawTo,
  SchematicDisplayOptions,
  SchematicSelectability,
} from "DrawCore/src/draw";
import {
  hitTestSrvArrow,
  nextViolationIndex,
  updateSrvArrowHover as updateSrvArrowHoverState,
  type SrvOverlay,
} from "DrawCore/src/content/schematic/srv_arrows";
import { cleanupWires } from "../../modes/wire_cleanup";
import { isValidPinName } from "DrawCore/src/validation";
import * as style from "DrawCore/src/style";

type Child = Instance | SchPin | WireSegment | Label | Dot | Annotation;
type Selectable = Instance | SchPin | WireSegment | Annotation; // FIXME: select more things!

// Visual marker drawn at the wire endpoint currently under the cursor so the
// user sees what `G` will grab. Intentionally smaller than the endpoint hit
// radius (ENDPOINT_HIT_RADIUS = 10 in wire.ts) so the clickable target is
// slightly larger than the visible ring — the standard "forgiving hit area"
// pattern. Matches STRETCH_INDICATOR_RADIUS in stretch_wire.ts so the marker
// looks the same before and after the user begins stretching. Rendered
// scale-invariant by dividing by the current zoom.
const ENDPOINT_HOVER_RADIUS = 8;
const endpointHoverStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  fill: style.Special.None,
  stroke: style.Color.new(255, 140, 0, 1.0),
  linewidth: 2,
};

//
// # Schematic — Render Tier
//
// The drawable state of a schematic: the wire index, the instance/pin/label/dot
// collections, in-progress provisionals, display options, annotation graphics,
// and the SRV overlay + dot-preview anchors the draw path reads. Extends
// `RenderContentBase` — the entity tree roots here, not on the `Schematic`
// facade. No selection/editing/modes; those live on `Schematic`.
//

//
// # Schematic Content — Interaction Tier
//
export class Schematic //
  extends InteractionBase<Child, Selectable, SchematicRender>
{
  override readonly contentKind = ContentKind.Schematic;

  private constructor(
    editor: InteractionHost,
    readonly symbols: schdata.SymbolMap
  ) {
    super(editor, SchematicRender.new(editor));
    // Route the wire index's change logging back through this facade; the
    // render-only (server) path leaves `onLogChange` unset and never logs.
    this.render.wires.onLogChange = (change) => {
      this.logChange(change);
    };
  }

  public properties?: Array<schdata.Property>;
  public constraints?: schema.schematic.Constraints;
  public pdkId?: string;
  public symbolImpl?: model.ident.Ident;
  public layoutImpls?: Array<schema.schematic.LayoutImplRef>;

  // Per-category selectability. When a flag is false, items of that category
  // cannot be clicked, box-selected, or picked up by select-all.
  selectability = SchematicSelectability.default();

  // Annotation editing wrapper over the render tier's graphics.
  readonly annotations = new Annotations(
    this,
    undefined,
    this.render.annotations
  );

  // --- Delegating getters to the render tier ---
  get wires(): Wires {
    return this.render.wires;
  }
  get instances(): Set<Instance> {
    return this.render.instances;
  }
  get pins(): Set<SchPin> {
    return this.render.pins;
  }
  get labels(): Set<Label> {
    return this.render.labels;
  }
  get dots(): Set<Dot> {
    return this.render.dots;
  }
  get provisionalInstances(): Set<Instance> {
    return this.render.provisionalInstances;
  }
  get provisionalPins(): Set<SchPin> {
    return this.render.provisionalPins;
  }
  get schematicDisplay(): SchematicDisplayOptions {
    return this.render.schematicDisplay;
  }

  // Push the SRV overlay. Called from the IDE via `editor.setSrvOverlay`
  // whenever SRV is active and the SrvModel state changes.
  setSrvOverlay = (overlay: SrvOverlay): RedrawRequest => {
    this.render.srvOverlay = overlay;
    return this.requestRedraw();
  };

  // Clear the SRV overlay (SRV inactive: no results, panel hidden, or
  // highlights toggled off). Called via `editor.clearSrvOverlay`.
  clearSrvOverlay = (): RedrawRequest => {
    this.render.srvOverlay = null;
    return this.requestRedraw();
  };

  // Hit-test the SRV arrows at a world point; if one is hit, route the click to
  // the panel selection (via the overlay's callback) and report it consumed.
  // Returns false when no arrow is hit (or SRV is gated off), so the caller
  // falls through to normal entity hit-testing.
  //
  // One arrow can stand for several violations at the same point, so a click
  // *cycles*: it selects the violation after the currently-highlighted one,
  // wrapping, and starts at the first when the highlight is elsewhere (or
  // nowhere). No cursor state is kept — the current highlight is the cursor.
  handleSrvArrowClick = (worldPoint: Point): boolean => {
    const overlay = this.render.srvOverlay;
    if (overlay === null) return false;
    const indices = hitTestSrvArrow(worldPoint);
    if (indices === null || indices.length === 0) return false;

    const next = nextViolationIndex(indices, overlay.highlightedViolationIndex);
    if (next === null) return false;
    overlay.onArrowClick(next);
    return true;
  };

  // Update the hovered SRV arrow from the cursor position, reporting whether an
  // arrow is under the cursor and whether that changed since the last call.
  // Callers suppress their own hover markers while an arrow is hovered, so the
  // visible cue matches what a click will actually do (arrows consume the
  // click ahead of entity hit-testing — see SchematicIdle.handleMouseDown).
  updateSrvArrowHover = (
    worldPoint: Point
  ): { hovered: boolean; changed: boolean } => {
    if (this.render.srvOverlay === null)
      return { hovered: false, changed: false };
    return updateSrvArrowHoverState(worldPoint);
  };

  // Running count of added instances, for naming.
  numInstances: number = 0;

  // The "last added" for stuff that can be added anew.
  // Serves as the default when adding new ones.
  // These initial values should never be drawn;
  // they just serve as the initial default instances.
  lastInstanceData: schdata.Instance = schdata.Instance.null();
  lastSchPinSpec: schdata.SchPinSpec = schdata.SchPinSpec.default();

  static async fromSchema(
    editor: InteractionHost,
    data: schdata.SchematicJson
  ): Promise<Result<Schematic>> {
    // Two steps here: schdata.SchematicJson -> schdata.Schematic -> (our, editor) Schematic.
    // Use background context for client-side code (no request context available).
    const sch = await schdata.Schematic.fromSchema(
      Context.background(),
      data,
      editor.depot,
      "" // Browser uses root namespace
    );
    if (sch.err) return sch;
    return Result.Ok(Schematic.fromData(editor, sch.val));
  }

  // Create a (drawn) `Schematic` from the abstract data model
  static fromData(editor: InteractionHost, data: schdata.Schematic): Schematic {
    const sch = new Schematic(editor, data.symbols);

    sch.ident = data.ident;
    // Note: bbox is calculated from actual content below, after all entities are loaded.

    // Copy properties if they exist
    if (data.properties) {
      sch.properties = [...data.properties];
    }

    // Copy constraints if they exist
    if (data.constraints) {
      sch.constraints = data.constraints;
    }

    // Copy PDK identifier
    if (data.pdkId) {
      sch.pdkId = data.pdkId;
    }

    // Copy the backward reference to the symbol this schematic implements
    if (data.symbolImpl) {
      sch.symbolImpl = data.symbolImpl;
    }

    // Copy forward references to layouts that implement this schematic
    if (data.layoutImpls) {
      sch.layoutImpls = data.layoutImpls;
    }

    // Add all instances
    data.instances.forEach((instData) => {
      sch.addInstance(new Instance({ data: instData, parent: sch.render }));
    });
    {
      // Set the last instance data, if any
      const rev = data.instances.reverse();
      const last = rev.find((instData) => !!instData.symbol);
      if (!last) {
        // No good instances found.
        // Stick with the "null instance" used to initialize UiState.
      } else {
        sch.lastInstanceData = last;
      }
    }

    // Add all pins
    data.pins.forEach((pinSpec) => {
      sch.pins.add(sch.createPin(pinSpec));
    });

    // Add wireSegments
    data.wireSegments.forEach((segData) => {
      sch.wires.add(
        sch.wires.provisional(segData),
        false // Note we do not want these in the changeLog!
      );
    });

    // Add annotation graphics
    data.graphics.forEach((element) =>
      sch.annotations.graphicsEntity.add(element)
    );

    // Infer dot locations from wire geometry. Dots are purely visual (T-junctions,
    // 3+ wire intersections) and have no semantic meaning — always infer fresh on load
    // rather than relying on stored dot data which can drift out of sync.
    sch.render.dots = Dots.infer(sch.render);

    // Bbox is computed in the model layer during deserialization.
    sch.render.bbox = data.bbox;

    // And return the new schematic
    return sch;
  }

  // Export to the abstract data model
  toData = (): schdata.Schematic => {
    const data = schdata.Schematic.fromPartial({ ident: this.ident });
    data.bbox = schdata.Bbox.clone(this.bbox);

    // Save all of our primary data structures `data` elements
    data.instances = Array.from(this.instances).map((x) => x.data);
    data.wireSegments = Array.from(this.wires.all()).map((x) => x.data);
    // Dots are always inferred from wire geometry on load — no need to save them.
    data.dots = [];
    data.pins = Array.from(this.pins).map((x) => x.data);
    data.graphics = this.annotations.graphicsEntity.toData();
    // Copy properties if they exist
    if (this.properties) {
      data.properties = [...this.properties];
    }
    // Copy constraints if they exist.
    if (this.constraints) {
      data.constraints = this.constraints;
    }
    return data;
  };

  // Toggle a schematic display option by key.
  // Mutates the shared object in-place; the canvas holds the same reference.
  toggleSchematicDisplay = (key: keyof SchematicDisplayOptions): void => {
    this.schematicDisplay[key] = !this.schematicDisplay[key];
    this.editor.canvas.invalidate?.();
    this.editor.requestRedraw();
  };

  // Toggle a per-category selectability flag.
  // Items of categories with `false` cannot be selected (hit-tests skip them).
  toggleSelectability = (key: keyof SchematicSelectability): void => {
    this.selectability[key] = !this.selectability[key];
    this.editor.requestRedraw();
  };

  // Add an entity to the schematic. Largely dispatches according to the entity's kind.
  // The return value is meant to be disregarded.
  override addEntity = (entity: Child, logChange: boolean): unknown => {
    const { entityKind } = entity;
    switch (entityKind) {
      // Delete-able entities
      case EntityKind.SchPin:
        return this.pins.add(entity);
      case EntityKind.Dot:
        return this.dots.add(entity);
      case EntityKind.WireSegment:
        return this.wires.add(entity, logChange);
      case EntityKind.Instance:
        return this.addInstance(entity);
      // Non directly add-able "child" entities
      case EntityKind.TextLabel:
      case EntityKind.ExprLabel:
      case EntityKind.EvalLabel:
        return this.labels.add(entity);
      case EntityKind.Annotation:
        return this.annotations.add(entity);
      default:
        exhaust(entityKind); // Exhaustiveness check
    }
  };

  // Remove an entity from the schematic. Largely dispatches according to the entity's kind.
  // Returns a boolean indication of whether something was deleted, similar to `Set.delete`.
  override removeEntity = (entity: Child, logChange: boolean): boolean => {
    const { entityKind } = entity;
    switch (entityKind) {
      // Delete-able entities
      case EntityKind.Instance:
        return this.instances.delete(entity);
      case EntityKind.SchPin:
        return this.pins.delete(entity);
      case EntityKind.Dot: {
        const rv = this.dots.delete(entity);
        this.updateDots();
        return rv;
      }
      case EntityKind.WireSegment: {
        const rv = this.wires.delete(entity, logChange);
        this.updateDots();
        return rv;
      }
      case EntityKind.TextLabel:
      case EntityKind.ExprLabel:
      case EntityKind.EvalLabel:
        return this.labels.delete(entity);
      case EntityKind.Annotation:
        return this.annotations.remove(entity);
      default:
        exhaust(entityKind); // Exhaustiveness check
    }
  };

  // An `Update` change's target is the edited entity (type-erased to unknown on
  // the change). Re-select it on undo/redo so the Properties Panel follows the
  // edit. Scoped to instances and pins — the editable kinds the panel tracks.
  protected override selectableFromUpdateTarget = (
    target: unknown
  ): Child | null =>
    target instanceof Instance || target instanceof SchPin ? target : null;

  // Instances and pins are the schematic entities the Properties panel tracks;
  // expose them so a refresh can re-locate the selection by id. Wires, labels,
  // and dots lack stable ids and aren't panel-tracked, so they're omitted.
  protected override selectableEntities = (): Iterable<Child> => [
    ...this.instances,
    ...this.pins,
  ];

  // Annotations are wrapped in a fresh `Annotation` per hit-test, so match them
  // by their underlying primitive; everything else uses reference identity.
  // (Without this, shift-clicking a selected text can't toggle it off.)
  protected override findSelectionMatch = (entity: Child): Child | null => {
    if (this.selection.has(entity)) return entity;
    if (entity instanceof Annotation) {
      for (const e of this.selection) {
        if (e instanceof Annotation && e.prim === entity.prim) return e;
      }
    }
    return null;
  };

  // Add an instance to the schematic.
  addInstance = (instance: Instance): void => {
    this.instances.add(instance);
    this.numInstances += 1;
    // FIXME: need to also add Entities per Port and Label
  };

  // Find an instance by its data id, or null if not present.
  findInstance = (id: number): Instance | null =>
    Array.from(this.instances).find((i) => i.data.id === id) ?? null;

  // This is called at commit-time of each `Move`, `Add`, and `Delete` action.
  // Set while a Combo is being applied. Dot inference is a whole-schematic
  // recompute, and a Combo used to trigger one per leaf change — undoing a
  // single drag that logged five edits ran it five times.
  private dotsSuspended = false;

  // Remove and re-infer all `Dot`s in the schematic.
  // FIXME! we never *really* want to do this, it should be done incrementally. But it's a (slow) start.
  updateDots = (): RedrawRequest => {
    if (this.dotsSuspended) return this.requestRedraw();
    this.dots.forEach((e) => e.remove());
    this.render.dots = Dots.infer(this.render);
    // The freshly inferred dots are different objects from the ones the anchors
    // reference, so any surviving capture is meaningless. Committing a move
    // lands here, which is what restores the hidden dots.
    this.render.dotAnchors = null;
    return this.requestRedraw();
  };

  // Apply `inner`, deferring dot inference until it has finished. Nested
  // Combos defer to the outermost one.
  private withDeferredDots = (inner: () => unknown): unknown => {
    const outer = this.dotsSuspended;
    this.dotsSuspended = true;
    inner();
    this.dotsSuspended = outer;
    return outer ? this.requestRedraw() : this.updateDots();
  };

  // Create a new `SchPin` from a `Spec`
  createPin = (pinSpec: schdata.SchPinSpec) =>
    new SchPin({ data: pinSpec, parent: this.render });

  // Add a pin at a given position with specified name and direction.
  // Used by the IDE "Add Pin" dialog. Enters AddSchPin mode so user can move it before committing.
  addPinAtPosition = (params: {
    name: string;
    direction: schdata.PortDir;
    position: { x: number; y: number };
  }): void => {
    const { name, direction, position } = params;

    // Validate pin name before creating
    if (!isValidPinName(name)) {
      this.editor.platform.warn(
        `[Schematic] Invalid pin name: "${name}". Must start with a letter and contain only alphanumeric characters and underscores.`
      );
      return;
    }

    // Check for duplicate pin names
    const existingPin = Array.from(this.pins).find(
      (p) => p.data.portName === name
    );
    if (existingPin) {
      this.editor.platform.warn(
        `[Schematic] A pin named "${name}" already exists`
      );
      return;
    }

    // Enter AddSchPin mode with the pre-configured spec so user can move it around
    AddSchPin.enterWithSpec(this, { name, direction, position });
  };

  // Enter "Add Pin" mode - places a new pin at the cursor position.
  // Called by command center to avoid simulating keyboard events.
  enterAddPinMode = (): void => {
    AddSchPin.enter(this);
  };

  // Enter "Draw Wire" mode - starts drawing a wire from the cursor position.
  // Called by command center to avoid simulating keyboard events.
  enterDrawWireMode = (): void => {
    this.editor.setMode(DrawWire.start(this));
  };

  // Default height for wire labels, consistent with other label heights in the codebase.
  private static readonly DEFAULT_WIRE_LABEL_HEIGHT = 16;

  // Deep copy a label for undo state preservation.
  private copyLabel = (label: graphics.Text): graphics.Text => ({
    ...label,
    loc: { ...label.loc },
  });

  // Update the label on a wire by wire ID.
  // Called by the IDE's EditWireLabelDialog after user confirms the new label.
  updateWireLabel = (wireId: number, newLabelText: string): void => {
    const { editor } = this;
    if (editor.isLocked) return;

    // Find the wire by ID by iterating over all wires
    let wire: WireSegment | null = null;
    for (const w of this.wires.all()) {
      if (w.data.id === wireId) {
        wire = w;
        break;
      }
    }
    if (!wire) {
      logger.warn(
        `[Schematic.updateWireLabel] Wire with ID ${wireId} not found`
      );
      return;
    }

    // Save old labels for undo (deep copy to preserve undo state)
    const oldLabels = wire.data.labels.map(this.copyLabel);

    // Create new labels array.
    // Note: When editing, only the first label's text is updated; additional
    // labels are preserved unchanged. This matches the single-label editing UI.
    let newLabels: graphics.Text[];
    if (newLabelText === "") {
      // Empty text means remove all labels
      newLabels = [];
    } else if (wire.data.labels.length > 0) {
      // Update existing label text, preserve position and other labels
      newLabels = wire.data.labels.map((label, idx) =>
        idx === 0
          ? { ...this.copyLabel(label), text: newLabelText }
          : this.copyLabel(label)
      );
    } else {
      // Create a new label at the wire midpoint.
      const midpoint = model.schematic.wireLabelLoc(wire.start(), wire.end());

      const newLabel: graphics.Text = {
        text: newLabelText,
        kind: graphics.ElementKind.Text,
        loc: midpoint,
        height: Schematic.DEFAULT_WIRE_LABEL_HEIGHT,
        hidden: false,
        alignment: "center",
        baseline: "bottom",
        rotation: 0,
      };
      newLabels = [newLabel];
    }

    // Check if there's actually a change
    const oldText = oldLabels.length > 0 ? oldLabels[0].text : "";
    if (newLabelText === oldText) return;

    // Apply the update
    wire.update({ labels: newLabels });

    // Log the change for undo/redo
    this.logChange(
      changes.update({
        target: wire,
        from: { labels: oldLabels },
        to: { labels: newLabels },
      })
    );

    editor.requestRedraw();
  };

  // Add a text annotation carrying `text`, then enter cursor-attached placement
  // (see startPlaceText). Called from the IDE after the Add Text dialog; the
  // provisional snaps to the cursor, so `position` is just a starting point.
  addTextAnnotation = (
    position: { x: number; y: number },
    text: string
  ): void => {
    const { editor } = this;
    if (editor.isLocked) return;
    if (text === "") return; // Nothing to add for empty text.
    this.annotations.startPlaceText(position, text);
  };

  // Update the string of an existing text annotation (by element id).
  // Called from the IDE after the user confirms the Edit Text dialog.
  // An empty string removes the annotation.
  updateTextAnnotation = (elementId: number, text: string): void => {
    const { editor } = this;
    if (editor.isLocked) return;
    this.annotations.updateText(elementId, text);
  };

  private convertChange = async (
    change: model.SchematicChange
  ): AsyncResult<changes.Single<Child> | null> => {
    const { changeKind } = change;
    switch (changeKind) {
      case "add": {
        const { targetKind } = change.data;
        switch (targetKind) {
          case "instance": {
            const instanceSpec: schdata.InstanceSpec = change.data;
            const instanceResult = await this.createInstance(instanceSpec);
            if (instanceResult.err) return instanceResult;
            return Result.Ok(changes.Add.new(instanceResult.val));
          }
          case "schpin": {
            const pinSpec: schdata.SchPinSpec = change.data;
            const pin = this.createPin(pinSpec);
            return Result.Ok(changes.Add.new(pin));
          }
          case "wire": {
            const wireData: schdata.WireSegment = change.data;
            const wire = this.wires.provisional(wireData);
            this.wires.add(wire, false);
            return Result.Ok(changes.Add.new(wire));
          }
          case "graphics": {
            // Materialize the annotation element and wrap it for the change
            // pipeline. `graphicsEntity.add` creates + registers the primitive;
            // the subsequent applyChange re-adds it idempotently (Set-backed).
            const { targetKind: _targetKind, ...element } = change.data;
            this.annotations.graphicsEntity.add(element);
            const prim =
              element.id !== undefined
                ? this.annotations.findPrimitive(element.id)
                : null;
            if (!prim) {
              return Result.Err({
                why: "not-found",
                message: `Failed to add graphics element ${element.id}`,
              });
            }
            return Result.Ok(changes.Add.new(new Annotation(prim)));
          }
          case "constraint": {
            // Apply constraint add locally for immediate UI feedback.
            // Generate ID client-side (server's IdMap preserves existing IDs).
            const spec = change.data as {
              id?: number;
              description?: string;
              constraintType?: string;
              firstInstances?: string[];
              secondInstances?: string[];
              groupId?: number;
            };
            const id = spec.id ?? model.randomId();
            this.applyConstraintAdd({ ...spec, id });
            return Result.Ok(null);
          }
          case "constraint_group": {
            const groupSpec = change.data;
            const groupId = groupSpec.id ?? model.randomId();
            this.applyConstraintGroupAdd({ ...groupSpec, id: groupId });
            return Result.Ok(null);
          }
          default:
            exhaust(targetKind);
        }
      }
      case "remove": {
        const { data, id } = change;
        switch (data.targetKind) {
          case "instance": {
            const instances = Array.from(this.instances);
            const entityToRemove = instances.find(
              (inst) => inst.data.id === id
            );
            if (!entityToRemove) {
              return Result.Err({
                why: "not-found",
                message: `Could not find instance with reference ${id} to remove`,
              });
            }
            return Result.Ok(changes.Remove.new(entityToRemove));
          }
          case "schpin": {
            const pins = Array.from(this.pins);
            const entityToRemove = pins.find((pin) => pin.data.id === id);
            if (!entityToRemove) {
              return Result.Err({
                why: "not-found",
                message: `Could not find pin with reference ${id} to remove`,
              });
            }
            return Result.Ok(changes.Remove.new(entityToRemove));
          }
          case "wire": {
            const wires = Array.from(this.wires.all());
            const entityToRemove = wires.find((wire) => wire.data.id === id);
            if (!entityToRemove) {
              return Result.Err({
                why: "not-found",
                message: `Could not find wire with reference ${id} to remove`,
              });
            }
            return Result.Ok(changes.Remove.new(entityToRemove));
          }
          case "graphics": {
            const prim = this.annotations.findPrimitive(id);
            if (!prim) {
              return Result.Err({
                why: "not-found",
                message: `Could not find graphics element ${id} to remove`,
              });
            }
            return Result.Ok(changes.Remove.new(new Annotation(prim)));
          }
          case "constraint": {
            // Apply constraint remove locally for immediate UI feedback.
            this.applyConstraintRemove(id);
            return Result.Ok(null);
          }
          case "constraint_group": {
            this.applyConstraintGroupRemove(id);
            return Result.Ok(null);
          }
          default:
            exhaust(data.targetKind);
        }
      }
      case "update": {
        const { targetKind } = change.data;
        switch (targetKind) {
          case "instance": {
            const instances = Array.from(this.instances);
            const entityToUpdate = instances.find(
              (inst) => inst.data.id === change.id
            );
            if (!entityToUpdate) {
              return Result.Err({
                why: "not-found",
                message: `Could not find instance with reference ${change.id} to update`,
              });
            }

            const updateData: Partial<schdata.Instance> = { ...change.data };
            // e.g. if we ask to replace a PMOS with an NMOS
            if (
              updateData.target &&
              updateData.target !== entityToUpdate.data.symbol?.ident
            ) {
              const symbolResult = await this.getSymbol(updateData.target);
              if (symbolResult.err) return symbolResult;
              updateData.symbol = symbolResult.val;
            }

            // Re-evaluate expressions whenever params, name, or symbol change.
            // The rendered param labels (e.g. "vt=lvt") come from `evals`, which
            // are computed from the symbol's exprLabels + instance params/name.
            // Without this re-eval, param changes don't update the canvas.
            if (updateData.params || updateData.name || updateData.symbol) {
              const newInstanceData = { ...entityToUpdate.data, ...updateData };
              schdata.Instance.evaluateExprs(newInstanceData);
              updateData.evals = newInstanceData.evals;
            }

            return Result.Ok(
              changes.update({
                target: entityToUpdate,
                from: entityToUpdate.data,
                to: updateData,
              })
            );
          }
          case "schpin": {
            const pins = Array.from(this.pins);
            const entityToUpdate = pins.find(
              (pin) => pin.data.id === change.id
            );
            if (!entityToUpdate) {
              return Result.Err({
                why: "not-found",
                message: `Could not find pin with reference ${change.id} to update`,
              });
            }
            return Result.Ok(
              changes.update({
                target: entityToUpdate,
                from: entityToUpdate.data,
                to: change.data,
              })
            );
          }
          case "wire": {
            const wires = Array.from(this.wires.all());
            const entityToUpdate = wires.find(
              (wire) => wire.data.id === change.id
            );
            if (!entityToUpdate) {
              return Result.Err({
                why: "not-found",
                message: `Could not find wire with reference ${change.id} to update`,
              });
            }
            return Result.Ok(
              changes.update({
                target: entityToUpdate,
                from: entityToUpdate.data,
                to: change.data,
              })
            );
          }
          case "graphics": {
            const prim = this.annotations.findPrimitive(change.id);
            if (!prim) {
              return Result.Err({
                why: "not-found",
                message: `Could not find graphics element ${change.id} to update`,
              });
            }
            const { targetKind: _targetKind, ...data } = change.data;
            return Result.Ok(
              changes.update({ target: prim, from: prim.data, to: data })
            );
          }
          case "constraint": {
            // Apply constraint update locally for immediate UI feedback.
            const spec = change.data as {
              id?: number;
              description?: string;
              constraintType?: string;
              firstInstances?: string[];
              secondInstances?: string[];
              groupId?: number;
            };
            this.applyConstraintUpdate(change.id, spec);
            return Result.Ok(null);
          }
          case "constraint_group": {
            const groupSpec = change.data as {
              description?: string;
              symmetryDirection?: "vertical" | "horizontal";
              symmetryAxisPos?: number;
            };
            this.applyConstraintGroupUpdate(change.id, groupSpec);
            return Result.Ok(null);
          }
          default:
            return Result.Err({
              why: "not-implemented",
              message: `Failed to update ${targetKind}`,
            });
        }
      }
      case "unsupported":
        return Result.Ok(null);
      default:
        exhaust(changeKind);
    }
  };
  // Convert a change-request message into one of our `Combo` in-memory changes, with pointers to real objects and everything
  private convertChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<changes.Combo<Child> | null> => {
    const changeClassList: Array<changes.Single<Child>> = [];
    for (const change of changeList) {
      const r = model.SchematicChange.validator.validate(change);
      const result = await r.andThenAsync((change) =>
        this.convertChange(change)
      );
      // FIXME: like Promises.all or whatever kicks them off in parallel
      // FIXME: we actually want to check that *all* changes work, since they can depend on one another.
      // And if not, reject the whole transaction.
      if (result.err || !result.val) continue;
      changeClassList.push(result.val);
    }
    const [first, ...rest] = changeClassList;
    if (!first) return null;
    return changes.Combo.new(first, ...rest);
  };

  override applyChangeRequest = async (
    changeList: Array<unknown>
  ): AsyncResult<null> => {
    // Convert the message-stuff into our in-memory classes
    const combo = await this.convertChangeRequest(changeList);
    if (!combo) return Result.Ok(null);

    // And apply it
    this.applyChange(combo);
    return Result.Ok(null);
  };

  // Apply a list of server-originated changes locally AND push them to the
  // undo stack as a single Combo, without re-sending to the server (it's
  // already there). See ContentBase.applyServerOriginatedChange for the full
  // rationale.
  override applyServerOriginatedChange = async (
    rawChanges: Array<unknown>
  ): AsyncResult<unknown> => {
    const combo = await this.convertChangeRequest(rawChanges);
    if (!combo) {
      return Result.Err({
        why: "convert-failed",
        message: "Failed to convert server-originated changes",
      });
    }
    this.applyChange(combo);
    // logChange would also call sendChangeMessage, which would round-trip the
    // change back to the server. Use recordChange to push to changeLog and
    // emit the [undo-log] line without re-sending.
    this.recordChange(combo);
    return Result.Ok(null);
  };

  // Apply a `Change`, generally as part of an undo or redo operation.
  override applyChange = (change: changes.Change<Child>): unknown => {
    const inner = () => {
      switch (change.kind) {
        case changes.Kind.Add:
          this.addEntity(change.entity, false);
          // FIXME: update dots incrementally, instead of re-inferring them all!
          return this.updateDots();
        // return change.entity.redraw(); // FIXME: signal we need to redraw

        case changes.Kind.Remove:
          this.removeEntity(change.entity, false);
          // FIXME: update dots incrementally, instead of re-inferring them all!
          return this.updateDots();

        case changes.Kind.Update: {
          change.apply(); // Reminder when undo'ing, this is the *inverse* change! Always `apply`.
          // FIXME: update dots incrementally, instead of re-inferring them all!
          return this.updateDots();
        }
        case changes.Kind.Combo: {
          return this.withDeferredDots(() => {
            change.changes.forEach(this.applyChange);
          });
        }
        default:
          exhaust(change); // Exhaustiveness check
      }
    };
    inner();
    return null;
  };

  // Select all entities whose bounding boxes intersect the given bbox.
  override selectAllInBbox = (
    bbox: model.Bbox,
    additive: boolean
  ): RedrawRequest | null => {
    const hits: Array<Child> = [];

    // A category can only be selected when it is BOTH visible and selectable.
    // Hidden items shouldn't be clickable; locked items shouldn't either.
    if (this.schematicDisplay.instances && this.selectability.instances) {
      for (const instance of this.instances) {
        if (instance.intersectsBbox(bbox)) {
          hits.push(instance);
        }
      }
    }

    if (this.schematicDisplay.pins && this.selectability.pins) {
      for (const pin of this.pins) {
        if (model.Bbox.intersects(bbox, pin.bbox)) {
          hits.push(pin);
        }
      }
    }

    if (this.schematicDisplay.wires && this.selectability.wires) {
      for (const wire of this.wires.all()) {
        if (model.Bbox.intersects(bbox, wire.bbox)) {
          hits.push(wire);
        }
      }
    }

    // Text annotations, wrapped in `Annotation` to match the single-click
    // selection path.
    for (const text of this.annotations.texts) {
      if (model.Bbox.intersects(bbox, graphics.Text.bbox(text.data))) {
        hits.push(new Annotation(text));
      }
    }

    return this.selectMultiple(hits, additive);
  };

  // Select every selectable entity in the schematic.
  // Respects both visibility and selectability category flags.
  override selectAll = (): RedrawRequest | null => {
    const hits: Array<Child> = [];

    if (this.schematicDisplay.instances && this.selectability.instances) {
      for (const instance of this.instances) hits.push(instance);
    }
    if (this.schematicDisplay.pins && this.selectability.pins) {
      for (const pin of this.pins) hits.push(pin);
    }
    if (this.schematicDisplay.wires && this.selectability.wires) {
      for (const wire of this.wires.all()) hits.push(wire);
    }

    return this.selectMultiple(hits, false);
  };

  // Hit test all schematic entities.
  // Returns the "highest priority" entity that is hit, or `null` if none are hit.
  override whatdWeHit = (mousePos: MousePos): Selectable | null => {
    // Check all Instance Labels
    // FIXME! add labels back
    // for (const instance of this.instances) {
    //   for (const label of instance.labels()) {
    //     if (label.hitTest(mousePos)) {
    //       return label;
    //     }
    //   }
    // }
    // Check all Port Labels
    // FIXME! add labels back
    // for (const port of this.ports) {
    //   for (const label of port.labels()) {
    //     if (label.hitTest(mousePos)) {
    //       return label;
    //     }
    //   }
    // }
    // Check all schematic labels
    // for (const label of this.labels) {
    //   if (label.hitTest(mousePos)) {
    //     return label;
    //   }
    // }
    // Check all Instance symbols / bodies.
    // Hidden categories are also unhittable (you can't click what isn't drawn).
    if (this.schematicDisplay.instances && this.selectability.instances) {
      for (const instance of this.instances) {
        if (instance.hitTest(mousePos)) {
          return instance;
        }
      }
    }
    // Check all pin symbols / bodies
    if (this.schematicDisplay.pins && this.selectability.pins) {
      for (const pin of this.pins) {
        if (pin.hitTest(mousePos)) {
          return pin;
        }
        // if (pin.label?.hitTest(mousePos)) {
        //   return pin.label;
        // }
      }
    }
    // Check all Wires
    if (this.schematicDisplay.wires && this.selectability.wires) {
      for (const wire of this.wires.all()) {
        if (wire.hitTest(mousePos)) {
          return wire;
        }
        for (const label of wire.labels) {
          if (label.hitTest(mousePos)) {
            return wire;
          }
        }
      }
    }
    // Check text annotations. Wrapped in an `Annotation` so they flow through
    // the shared selection/move/change machinery like other graphics elements.
    for (const text of this.annotations.texts) {
      if (text.hitTest(mousePos)) {
        return new Annotation(text);
      }
    }
    // Didn't hit anything, return null.
    return null;
  };

  // Flip all selected flippable entities.
  flipSelected = (dir: schdata.Direction) => {
    const singleChanges: Array<changes.Single<Child>> = [];

    for (const entity of this.selection) {
      // Filter down to "flippable" entities.
      const { entityKind } = entity;
      if (
        entityKind !== EntityKind.Instance &&
        entityKind !== EntityKind.SchPin
      ) {
        continue;
      }

      // Clone location data before and after flip for change logging.
      const xformFrom = schdata.Xform.clone(entity.xform);
      entity.flip(dir);
      const xformTo = schdata.Xform.clone(entity.xform);

      singleChanges.push(
        changes.update({
          target: entity,
          from: { xform: xformFrom },
          to: { xform: xformTo },
        })
      );
    }

    if (singleChanges.length === 1) {
      this.logChange(singleChanges[0]);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok) this.logChange(combo.val);
    }
  };

  // Rotate all selected rotatable entities by 90 degrees.
  rotateSelected = () => {
    const singleChanges: Array<changes.Single<Child>> = [];

    for (const entity of this.selection) {
      const xformable = this.xformable(entity);
      if (!xformable) continue;

      // Clone location data before and after rotation for change logging.
      const xformFrom = schdata.Xform.clone(xformable.xform);
      xformable.rotate();
      const xformTo = schdata.Xform.clone(xformable.xform);

      singleChanges.push(
        changes.update({
          target: xformable,
          from: { xform: xformFrom },
          to: { xform: xformTo },
        })
      );
    }

    if (singleChanges.length === 1) {
      this.logChange(singleChanges[0]);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok) this.logChange(combo.val);
    }
  };

  // Duplicate all selected instances, placing the copies at an offset from the originals.
  // The offset is 50% of the selection bounding box size in each axis (minimum 100 units).
  override duplicateSelected = (): RedrawRequest | null => {
    if (this.selection.size === 0) return null;

    // Collect only Instance entities from the selection (skip wires, pins, etc.)
    const instances: Array<Instance> = [];
    for (const entity of this.selection) {
      if (entity instanceof Instance) {
        instances.push(entity);
      }
    }
    if (instances.length === 0) return null;

    // Compute the offset for duplicate placement.
    // Use the maximum individual instance bbox dimensions (not the union of all instances).
    // Using the union bbox causes the offset to scale with inter-item distance,
    // producing unpredictable results when items are spread apart.
    let maxWidth = 0;
    let maxHeight = 0;
    for (const inst of instances) {
      const b = inst.bbox;
      maxWidth = Math.max(maxWidth, b.max.x - b.min.x);
      maxHeight = Math.max(maxHeight, b.max.y - b.min.y);
    }
    const dx = Math.max(maxWidth * 0.5, 100);
    const dy = Math.max(maxHeight * 0.5, 100);

    // Materialize the duplicates via the shared helper (same code path as
    // paste): clone, incrementally rename, fresh id, offset, re-eval exprs.
    const newInstances = this.materializeInstances(
      instances.map((inst) => inst.data),
      { dx, dy },
      this.instanceNameSet()
    );

    // Log all additions as a single atomic change (undoable in one Ctrl+Z)
    this.logAdds(newInstances.map((inst) => changes.add(inst)));

    // Select the newly created instances
    this.selectMultiple(newInstances);
    return this.requestRedraw();
  };

  // ---------------------------------------------------------------------
  // Copy / paste (issue #2037)
  //
  // The clipboard payload itself lives in an app-wide singleton (see
  // `./clipboard`) so it survives mode transitions, content reloads, and
  // tab switches, and so a copy in one schematic can be pasted into
  // another (PDK-gated, see below). These methods live on the stable
  // `Schematic` content object rather than the transient `SchematicIdle`
  // mode handler, which `goUiIdle()` re-creates on every transition.
  // ---------------------------------------------------------------------

  // Snapshot the currently-selected instances, wires, and SchPins into the
  // shared clipboard. Labels follow their wires (already in the wire's
  // `labels` field); dots are re-derived; annotations are out of scope for
  // v1 per the proposal.
  copySelected = (): void => {
    if (this.selection.size === 0) return;

    const instances: schdata.Instance[] = [];
    const wires: schema.schematic.WireSegment[] = [];
    const pins: schdata.SchPinSpec[] = [];

    for (const entity of this.selection) {
      if (entity instanceof Instance) {
        instances.push(cloneInstanceData(entity.data));
      } else if (entity instanceof WireSegment) {
        wires.push(cloneWireData(entity.data));
      } else if (entity instanceof SchPin) {
        pins.push(schdata.SchPinSpec.clone(entity.data));
      }
      // Skip Labels, Dots, Annotations — out of v1 scope.
    }

    const count = instances.length + wires.length + pins.length;
    if (count === 0) return;

    schematicClipboard.set({
      payload: { instances, wires, pins },
      sourcePdkId: this.pdkId,
    });
    this.editor.showItemFeedback("Copied", count);
  };

  // Paste the shared clipboard snapshot into this schematic at a fixed grid
  // offset. Mints incremental names and fresh ids for the new instances and
  // SchPins so they don't collide with existing entities. All resulting Add
  // changes are bundled into a single Combo so undo collapses the paste to
  // one step. No-op if the clipboard is empty.
  //
  // Cross-PDK gate: when the clipboard's source PDK and this schematic's PDK
  // are both set and incompatible, PDK-specific (libcellview) instances are
  // dropped; builtins (nmos/pmos/res/gnd/…), wires, and pins still paste, and
  // the user is told how many were skipped.
  paste = (): void => {
    const entry = schematicClipboard.get();
    if (!entry) return;
    const { instances, wires, pins } = entry.payload;
    const { editor } = this;

    // Fixed offset, mirroring the layout side's `snapX * 10`. Smaller factor
    // here because schematic grids tend to be coarser.
    const offset = editor.grid.settings.snapX * 4;

    // Per-symbol PDK gate; wires and pins are PDK-agnostic and always paste.
    const pasteableInstances = instances.filter((data) =>
      canPasteSymbolIntoPdk(
        instanceSymbolPdk(data.target, entry.sourcePdkId),
        this.pdkId
      )
    );
    const skipped = instances.length - pasteableInstances.length;

    // All resulting Add changes, and the new entities to select afterwards.
    const adds: Array<changes.Single<Child>> = [];
    const newSelectables: Array<Selectable> = [];

    // Instances — shared materializer (same path as duplicate).
    const newInstances = this.materializeInstances(
      pasteableInstances,
      { dx: offset, dy: offset },
      this.instanceNameSet()
    );
    for (const inst of newInstances) {
      adds.push(changes.add(inst));
      newSelectables.push(inst);
    }

    // Wires.
    for (const data of wires) {
      const newEndpoints: [Point, Point] = [
        { x: data.endpoints[0].x + offset, y: data.endpoints[0].y + offset },
        { x: data.endpoints[1].x + offset, y: data.endpoints[1].y + offset },
      ];
      const newData: schema.schematic.WireSegment = {
        ...data,
        id: randomId(),
        endpoints: newEndpoints,
        style: { ...data.style },
        // Recompute label positions from the pasted (offset) geometry so labels
        // stay with their wire, exactly as if the wire were freshly drawn here.
        labels: model.schematic.recenterLabels(
          data.labels,
          newEndpoints[0],
          newEndpoints[1]
        ),
      };
      const wire = this.wires.provisional(newData);
      // logChange=false: bypass split/merge — the proposal explicitly says
      // pasted wires never auto-merge into existing nets, even at coincident
      // endpoints. We log the Add ourselves via the combo below.
      this.wires.add(wire, false);
      adds.push(changes.add(wire));
      newSelectables.push(wire);
    }

    // Pins.
    const existingPinNames = this.pinNameSet();
    for (const spec of pins) {
      const newPortName = nextFreeName(spec.portName, existingPinNames);
      existingPinNames.add(newPortName);
      const newSpec: schdata.SchPinSpec = {
        ...schdata.SchPinSpec.clone(spec),
        portName: newPortName,
        id: randomId(),
        xform: {
          ...spec.xform,
          x: spec.xform.x + offset,
          y: spec.xform.y + offset,
        },
      };
      const pin = this.createPin(newSpec);
      this.pins.add(pin);
      adds.push(changes.add(pin));
      newSelectables.push(pin);
    }

    if (adds.length === 0) {
      if (skipped > 0) {
        editor.showFeedback(`Skipped ${skipped} (PDK mismatch)`);
      }
      return;
    }

    this.logAdds(adds);

    // Refresh solder dots — endpoints may now produce new T-junctions.
    this.updateDots();
    this.selectMultiple(newSelectables);

    if (skipped > 0) {
      editor.showFeedback(
        `Pasted ${adds.length}, skipped ${skipped} (PDK mismatch)`
      );
    }
  };

  // Build the set of existing instance names, for incremental naming.
  private instanceNameSet = (): Set<string> => {
    const names = new Set<string>();
    for (const inst of this.instances) names.add(inst.data.name);
    return names;
  };

  // Build the set of existing pin (port) names, for incremental naming.
  private pinNameSet = (): Set<string> => {
    const names = new Set<string>();
    for (const pin of this.pins) names.add(pin.data.portName);
    return names;
  };

  // Log a batch of Add changes as one atomic, single-undo step.
  private logAdds = (adds: Array<changes.Single<Child>>): void => {
    if (adds.length === 0) return;
    if (adds.length === 1) {
      this.logChange(adds[0]);
      return;
    }
    const combo = changes.Combo.fromArray(adds);
    if (combo.ok) this.logChange(combo.val);
  };

  // Shared creation path for duplicate and paste: for each source instance
  // data, clone it, give it an incremental collision-free name (updating
  // `existingNames` as we go so a batch doesn't collide with itself), a fresh
  // id, an offset position (orientation preserved), re-evaluate its expression
  // labels, build the Instance, and add it to the schematic. Returns the new
  // Instances; the caller is responsible for logging and selection.
  private materializeInstances = (
    datas: schdata.Instance[],
    offset: { dx: number; dy: number },
    existingNames: Set<string>
  ): Instance[] => {
    const created: Instance[] = [];
    for (const data of datas) {
      const newData = cloneInstanceData(data);
      newData.name = nextFreeName(newData.name, existingNames);
      existingNames.add(newData.name);
      // Fresh random id so the server's IdMap doesn't see a duplicate.
      newData.id = randomId();
      // Offset position; the spread preserves the orientation fields
      // (reflect/rotate) and only overrides x/y.
      newData.xform = {
        ...newData.xform,
        x: newData.xform.x + offset.dx,
        y: newData.xform.y + offset.dy,
      };
      // CRITICAL: the rendered instance-name label comes from `inst.evals`
      // (computed by Instance.evaluateExprs from the symbol's exprLabels),
      // not directly from `data.name`. Without this re-eval the canvas shows
      // the original name even though `data.name` is the new value.
      schdata.Instance.evaluateExprs(newData);
      const inst = new Instance({ data: newData, parent: this.render });
      this.addEntity(inst, false);
      created.push(inst);
    }
    return created;
  };

  // Compute the bounding box of the currently selected entities.
  override selectionBbox = (): model.Bbox | null => {
    if (this.selection.size === 0) return null;
    const bboxes: Array<model.Bbox> = [];
    for (const entity of this.selection) {
      // Instance, SchPin, and WireSegment all expose .bbox
      if ("bbox" in entity) {
        bboxes.push((entity as { bbox: model.Bbox }).bbox);
      }
    }
    return bboxes.length > 0 ? model.Bbox.getBboxUnion(bboxes) : null;
  };

  // Filter down to transformable entities. Return `null` if the entity is not transformable.
  xformable = (child: Child): Instance | SchPin | null => {
    // FIXME: should be like: (Child & schdata.Xformable) | null => {
    // Relies on getting `Entity` onto some of those other types!
    const { entityKind } = child;
    switch (entityKind) {
      case EntityKind.Instance:
      case EntityKind.SchPin:
        return child;
      default:
        return null;
    }
  };

  // Modify a pin to meet a new `pinSpec`
  updatePin = (pin: SchPin, pinSpec: schdata.SchPinSpec) => {
    // Update and re-draw the pin
    const oldPinSpec = pin.data;
    pin.update(pinSpec);

    // Make this the "most recent" for future additions
    this.lastSchPinSpec = schdata.SchPinSpec.clone(pinSpec);

    // And log the change
    this.logChange(
      changes.update({ target: pin, from: oldPinSpec, to: pinSpec })
    );
  };

  // Get all pins in the schematic as an array.
  getPins = (): SchPin[] => {
    return Array.from(this.pins);
  };

  // Rename a pin by its numeric ID.
  renamePinById = (pinId: number, newName: string): void => {
    // Validate new name before renaming
    if (!isValidPinName(newName)) {
      this.editor.platform.warn(
        `[Schematic] Invalid pin name: "${newName}". Must start with a letter and contain only alphanumeric characters and underscores.`
      );
      return;
    }

    // Check for duplicate pin names (excluding the pin being renamed)
    const existingPin = Array.from(this.pins).find(
      (p) => p.data.portName === newName && p.data.id !== pinId
    );
    if (existingPin) {
      this.editor.platform.warn(
        `[Schematic] A pin named "${newName}" already exists`
      );
      return;
    }

    const pin = Array.from(this.pins).find((p) => p.data.id === pinId);
    if (!pin) {
      this.editor.platform.warn(
        `[Schematic] renamePinById: pin with id ${pinId} not found`
      );
      return;
    }

    // Create an updated pin spec with the new name
    const newPinSpec: schdata.SchPinSpec = {
      ...pin.data,
      portName: newName,
    };

    this.updatePin(pin, newPinSpec);
  };

  // Update a pin's direction by ID. Mirrors `renamePinById` — same
  // `updatePin` mutation path, so undo/redo and change logging are free.
  //
  // Also refreshes `labelFmt` to the new direction's `defaultLabelFmt`.
  // Without this the label would stay on the side of the old direction
  // (e.g. left of origin for input) after flipping to output and land on
  // top of the new arrow body. The body graphics flip automatically via
  // `Data.symbol(pin.data)` in `SchPin.build()`.
  updatePinDirectionById = (pinId: number, newDir: schdata.PortDir): void => {
    const pin = Array.from(this.pins).find((p) => p.data.id === pinId);
    if (!pin) {
      this.editor.platform.warn(
        `[Schematic] updatePinDirectionById: pin with id ${pinId} not found`
      );
      return;
    }

    // No-op if the direction is unchanged.
    if (pin.data.direction === newDir) return;

    // Create an updated pin spec with the new direction and a matching
    // default label format for that direction.
    const newSymbol = schdata.SchPinSymbol.get(newDir);
    const newPinSpec: schdata.SchPinSpec = {
      ...pin.data,
      direction: newDir,
      labelFmt: newSymbol.defaultLabelFmt,
    };

    this.updatePin(pin, newPinSpec);
  };

  // Create a new `Instance` from a `Spec`
  //
  // This is a schematic-level method largely so that it can track down the symbol,
  // which is required by the `Instance`, but is not part of the `Spec`.
  //
  createInstance = async (
    instanceSpec: schdata.InstanceSpec
  ): AsyncResult<Instance> => {
    //
    // First, find the symbol, or return failure if we can't.
    const symbolResult = await this.getSymbol(instanceSpec.target);
    if (symbolResult.err) return symbolResult;
    const symbol = symbolResult.val;

    // Create our new `InstanceData`
    const instanceData: ResolvedInstanceData = {
      ...schdata.Instance.fromPartial(instanceSpec),
      symbol,
    };

    // Evaluate all the expressions
    schdata.Instance.evaluateExprs(instanceData);

    const inst = new Instance({ data: instanceData, parent: this.render });
    return Result.Ok(inst);
  };

  // Get the `Symbol` for `ident`, either from memory or from the platform.
  getSymbol = async (
    ident: model.Ident
  ): AsyncResult<schdata.Symbol, GetSymbolError> => {
    //
    // First look in local symbols already available in memory, in the schematic
    const get = this.symbols.get(ident);
    switch (get.answer) {
      case "here": // Got it!
        return Result.Ok(get.symbol);
      case "thats-not-a-thing": // Error time
        return Result.Err({
          why: "invalid-builtin",
          message: `Requested invalid builtin '${ident}'`,
        });
      case "not-found-here": // Not in memory, need to fetch it
        break;
      default:
        exhaust(get);
    }

    // Send off the request to the platform
    // How cool is this await stuff
    const getSymbolResult = await this.editor.requestSymbol(ident);

    if (getSymbolResult.err) {
      return Result.Err({
        why: "invalid-symbol",
        message: `Invalid symbol structure: ${JSON.stringify(
          getSymbolResult.val
        )}`,
      });
    }

    // Got it. Add it to the in-memory set, and return it.
    const symbol = getSymbolResult.val;
    this.symbols.insert(symbol);
    return Result.Ok(symbol);
  };

  idleMode = (): modes.ModeHandler => new SchematicIdle(this);

  static createNew(editor: InteractionHost): Schematic {
    return Schematic.fromData(
      editor,
      schdata.Schematic.fromPartial({
        ident: { kind: "hierpath" as const, path: [] },
      })
    );
  }

  // Apply a constraint add locally for immediate UI feedback.
  // Finds or creates a group based on constraintType, mirroring server logic.
  //
  // HACK: This duplicates the group-finding logic from AlphaServer's getOrCreateGroup.
  // The server enriches constraints with groupId, but since we exclude origin from
  // broadcasts (for optimistic UI), the client needs to determine groupId locally.
  // Keep CONSTRAINT_TYPE_DESCRIPTIONS in sync with:
  //   Editor/packages/State/src/stateService_impl/schematic/constraints/constraints.ts
  private applyConstraintAdd(spec: {
    id: number;
    description?: string;
    constraintType?: string;
    firstInstances?: string[];
    secondInstances?: string[];
    groupId?: number;
  }): void {
    if (!this.constraints) {
      this.constraints = { groups: [], matches: [] };
    }

    // If explicit groupId provided, find that group
    let targetGroup: schema.schematic.ConstraintGroup | undefined;
    if (spec.groupId !== undefined) {
      targetGroup = this.constraints.groups.find((g) => g.id === spec.groupId);
    }

    // Fall back to finding/creating by type description
    if (!targetGroup) {
      const CONSTRAINT_TYPE_DESCRIPTIONS: Record<string, string> = {
        differential: "differential",
        current_mirror: "current mirror",
        matched_load: "matched load",
        cross_coupled: "cross coupled",
        matched_devices: "matched devices",
      };

      const constraintType = spec.constraintType ?? "matched_devices";
      const groupDescription =
        CONSTRAINT_TYPE_DESCRIPTIONS[constraintType] ??
        constraintType.replace(/_/g, " ");

      targetGroup = this.constraints.groups.find(
        (g) => g.description === groupDescription
      );

      if (!targetGroup) {
        targetGroup = {
          id: randomId(),
          description: groupDescription,
          symmetryDirection: "vertical",
          matches: [],
        };
        this.constraints.groups.push(targetGroup);
      }
    }

    // Ensure matches array exists
    if (!targetGroup.matches) {
      targetGroup.matches = [];
    }

    // Add match to the group
    const match: schema.schematic.ConstraintMatch = {
      id: spec.id,
      description: spec.description,
      constraintType: spec.constraintType,
      firstInstances: spec.firstInstances ?? [],
      secondInstances: spec.secondInstances ?? [],
      groupId: targetGroup.id,
    };
    targetGroup.matches.push(match);
  }

  // Apply a constraint remove locally for immediate UI feedback.
  private applyConstraintRemove(id: number): void {
    if (!this.constraints) return;
    // Remove from standalone matches
    this.constraints.matches = this.constraints.matches.filter(
      (m: schema.schematic.ConstraintMatch) => m.id !== id
    );
    // Also remove from any group's matches array
    for (const group of this.constraints.groups) {
      if (group.matches) {
        group.matches = group.matches.filter(
          (m: schema.schematic.ConstraintMatch) => m.id !== id
        );
      }
    }
  }

  // Apply a constraint group add locally for immediate UI feedback.
  private applyConstraintGroupAdd(spec: {
    id: number;
    description?: string;
    symmetryDirection?: "vertical" | "horizontal";
    symmetryAxisPos?: number;
  }): void {
    if (!this.constraints) {
      this.constraints = { groups: [], matches: [] };
    }
    this.constraints.groups.push({
      id: spec.id,
      description: spec.description,
      symmetryDirection: spec.symmetryDirection ?? "vertical",
      symmetryAxisPos: spec.symmetryAxisPos,
      matches: [],
    });
  }

  // Apply a constraint group remove locally for immediate UI feedback.
  private applyConstraintGroupRemove(groupId: number): void {
    if (!this.constraints) return;
    this.constraints.groups = this.constraints.groups.filter(
      (g) => g.id !== groupId
    );
  }

  // Apply a constraint group update locally for immediate UI feedback.
  private applyConstraintGroupUpdate(
    groupId: number,
    spec: {
      description?: string;
      symmetryDirection?: "vertical" | "horizontal";
      symmetryAxisPos?: number;
    }
  ): void {
    if (!this.constraints) return;
    const group = this.constraints.groups.find((g) => g.id === groupId);
    if (!group) return;
    if (spec.description !== undefined) group.description = spec.description;
    if (spec.symmetryDirection !== undefined)
      group.symmetryDirection = spec.symmetryDirection;
    if (spec.symmetryAxisPos !== undefined)
      group.symmetryAxisPos = spec.symmetryAxisPos;
  }

  // Apply a constraint update locally for immediate UI feedback.
  private applyConstraintUpdate(
    id: number,
    spec: {
      description?: string;
      constraintType?: string;
      firstInstances?: string[];
      secondInstances?: string[];
      groupId?: number;
    }
  ): void {
    if (!this.constraints) return;

    // Helper to update a match's fields
    const updateMatch = (match: schema.schematic.ConstraintMatch): void => {
      if (spec.description !== undefined) match.description = spec.description;
      if (spec.constraintType !== undefined)
        match.constraintType = spec.constraintType;
      if (spec.firstInstances !== undefined)
        match.firstInstances = spec.firstInstances;
      if (spec.secondInstances !== undefined)
        match.secondInstances = spec.secondInstances;
      if (spec.groupId !== undefined) match.groupId = spec.groupId;
    };

    // Find the match anywhere (standalone or in a group)
    let foundMatch: schema.schematic.ConstraintMatch | undefined;
    let sourceGroup: schema.schematic.ConstraintGroup | undefined;

    // Check standalone matches
    foundMatch = this.constraints.matches.find(
      (m: schema.schematic.ConstraintMatch) => m.id === id
    );

    // Check group matches
    if (!foundMatch) {
      for (const group of this.constraints.groups) {
        if (!group.matches) continue;
        const groupMatch = group.matches.find(
          (m: schema.schematic.ConstraintMatch) => m.id === id
        );
        if (groupMatch) {
          foundMatch = groupMatch;
          sourceGroup = group;
          break;
        }
      }
    }

    if (!foundMatch) return;

    // Update the match fields
    updateMatch(foundMatch);

    // If groupId changed, move the match to the target group
    if (spec.groupId !== undefined && sourceGroup?.id !== spec.groupId) {
      // Remove from source group
      if (sourceGroup?.matches) {
        sourceGroup.matches = sourceGroup.matches.filter(
          (m: schema.schematic.ConstraintMatch) => m.id !== id
        );
      } else {
        // Remove from standalone
        this.constraints.matches = this.constraints.matches.filter(
          (m: schema.schematic.ConstraintMatch) => m.id !== id
        );
      }

      // Add to target group
      const targetGroup = this.constraints.groups.find(
        (g) => g.id === spec.groupId
      );
      if (targetGroup) {
        if (!targetGroup.matches) targetGroup.matches = [];
        targetGroup.matches.push(foundMatch);
      }
    }
  }

  // Apply buffered field edits from the Properties Panel as a single atomic
  // change (one undo step / one server change). Builds the merged new data for
  // the target entity, eager-applies it, and logs one Update change.
  override applyFieldEdits = (
    edits: FieldEdits
  ): Result<void, { why: string }> => {
    // Compare against the EntityKind string values (edits.entityKind is a plain
    // string from the host, so we avoid a cross-enum comparison).
    if (edits.entityKind === EntityKind.Instance.valueOf()) {
      const instance = Array.from(this.instances).find(
        (i) => i.data.id === edits.id
      );
      if (!instance) return Result.Err({ why: "instance-not-found" });
      return this.applyInstanceFieldEdits(instance, edits);
    }
    if (edits.entityKind === EntityKind.SchPin.valueOf()) {
      const pin = Array.from(this.pins).find((p) => p.data.id === edits.id);
      if (!pin) return Result.Err({ why: "pin-not-found" });
      return this.applySchPinFieldEdits(pin, edits);
    }
    return Result.Err({ why: "unsupported-entity-kind" });
  };

  // Build + apply + log a single merged Update for a schematic instance.
  private applyInstanceFieldEdits(
    instance: Instance,
    edits: FieldEdits
  ): Result<void, { why: string }> {
    const oldData = instance.data;

    // Validate name uniqueness if a name change is requested.
    if (edits.name !== undefined && edits.name !== oldData.name) {
      const clash = Array.from(this.instances).find(
        (i) => i.data.name === edits.name && i.data.id !== edits.id
      );
      if (clash) return Result.Err({ why: "duplicate-instance-name" });
    }

    // Build PARTIAL from/to dicts carrying only the changed fields — matching
    // the move-mode pattern (the server merges updates by id, and sending
    // runtime-computed fields like conns/symbol/bbox would be wrong).
    const from: Partial<model.schematic.Instance> = {};
    const to: Partial<model.schematic.Instance> = {};
    if (edits.name !== undefined) {
      from.name = oldData.name;
      to.name = edits.name;
    }
    if (
      edits.position !== undefined ||
      edits.rotation !== undefined ||
      edits.reflected !== undefined
    ) {
      from.xform = { ...oldData.xform };
      to.xform = applyXformEdits(oldData.xform, edits);
    }
    if (edits.params !== undefined) {
      const paramsArr = paramsRecordToArray(edits.params);
      if (paramsArr.err) return paramsArr;
      from.params = oldData.params;
      to.params = paramsArr.val;
    }

    // Re-evaluate expressions whenever name or params change. The rendered
    // instance labels (name, "vt=lvt", …) come from `evals`, computed from the
    // symbol's exprLabels + instance name/params. Without this re-eval the
    // canvas label stays stale after an edit. Mirrors the server-change path in
    // convertChange().
    if (edits.name !== undefined || edits.params !== undefined) {
      const newInstanceData = { ...oldData, ...to };
      schdata.Instance.evaluateExprs(newInstanceData);
      from.evals = oldData.evals;
      to.evals = newInstanceData.evals;
    }

    instance.update(to);
    this.logChange(changes.update({ target: instance, from, to }));
    return Result.Ok(undefined);
  }

  // Build + apply + log a single merged Update for a schematic pin.
  private applySchPinFieldEdits(
    pin: SchPin,
    edits: FieldEdits
  ): Result<void, { why: string }> {
    const oldData = pin.data;

    if (edits.name !== undefined && edits.name !== oldData.portName) {
      if (!isValidPinName(edits.name)) {
        return Result.Err({ why: "invalid-pin-name" });
      }
      const clash = Array.from(this.pins).find(
        (p) => p.data.portName === edits.name && p.data.id !== edits.id
      );
      if (clash) return Result.Err({ why: "duplicate-pin-name" });
    }

    // Direction change also refreshes labelFmt to the new direction's default,
    // mirroring updatePinDirectionById.
    const dirChanged =
      edits.direction !== undefined && edits.direction !== oldData.direction;
    const labelFmt = dirChanged
      ? schdata.SchPinSymbol.get(edits.direction ?? oldData.direction)
          .defaultLabelFmt
      : oldData.labelFmt;

    const newXform = applyXformEdits(oldData.xform, edits);
    const newData: schdata.SchPinSpec = {
      ...oldData,
      ...(edits.name !== undefined ? { portName: edits.name } : {}),
      ...(dirChanged
        ? { direction: edits.direction ?? oldData.direction }
        : {}),
      labelFmt,
      xform: newXform,
    };

    pin.update(newData);
    this.logChange(changes.update({ target: pin, from: oldData, to: newData }));
    return Result.Ok(undefined);
  }
}

// Apply position / rotation / reflected edits onto an existing schematic Xform,
// returning a new Xform (the unedited components are preserved).
function applyXformEdits(xform: model.Xform, edits: FieldEdits): model.Xform {
  return {
    x: edits.position !== undefined ? edits.position.x : xform.x,
    y: edits.position !== undefined ? edits.position.y : xform.y,
    rotation: edits.rotation ?? xform.rotation,
    reflected: edits.reflected ?? xform.reflected,
  };
}

// Convert a `Record<name, value>` params dict (values already validated/typed
// by the panel, but stored stringified on schematic instances) into the
// schematic `Array<InstanceParam>` shape. Non-string values are stringified;
// a non-primitive value is unexpected here and surfaces as an `Err`.
function paramsRecordToArray(
  params: Record<string, unknown>
): Result<Array<model.schematic.InstanceParam>, { why: string }> {
  const out: Array<model.schematic.InstanceParam> = [];
  for (const [name, value] of Object.entries(params)) {
    const str = paramValueToString(value);
    if (str.err) return str;
    out.push(model.schematic.InstanceParam.new(name, str.val));
  }
  return Result.Ok(out);
}

// # Idle Mode
//
// The typical state of sitting there, doing nothing.
// Primarily this state waits for actions to enter other modes.
// Clicking on entities selects them.
// Note having something `selected` is orthogonal to `Idle`;
// we can be in this state and have something selected.
//
export class SchematicIdle extends modes.ModeHandler {
  constructor(readonly schematic: Schematic) {
    super();
  }
  get editor() {
    return this.schematic.editor;
  }

  // The wire endpoint currently under the cursor (if any). Updated by
  // `handleMouseMove` below; read by the LMB and G handlers to enter
  // endpoint-stretch with the highlighted endpoint taking priority over
  // the normal hit-test. Lives on SchematicIdle (not UiState) so it
  // resets automatically on every mode transition: `Editor.goUiIdle()`
  // constructs a fresh `SchematicIdle`, so any reference to a wire that
  // was deleted in another mode can't survive back into idle.
  private hoveredWireEndpoint: { wire: WireSegment; index: 0 | 1 } | null =
    null;

  // We're already in idle mode.
  // On `abort`, just de-select anything highlighted.
  override abort = () => this.editor.content.deselect();

  // In idle mode, if we clicked on something, react and update our UI state.
  override handleMouseDown = (e: MouseEvent) => {
    const { editor, schematic } = this;

    // If a context menu is open, this click is dismissing it — don't start any interaction.
    if (editor.isAnyContextMenuVisible()) return;

    // Ctrl+Middle click: drag to zoom
    if (e.button === 1 && e.ctrlKey) {
      editor.modeHandler = modes.DragZoom.start(editor);
      return;
    }

    // Middle click: ALWAYS start panning, regardless of what's under cursor
    if (e.button === 1) {
      editor.modeHandler = modes.Pan.start(editor);
      return;
    }

    // SRV violation arrows take precedence over normal entity hit-testing: a
    // left-click on an arrow selects its row in the SrvPanel (no pan/zoom) and
    // consumes the click. When SRV is gated off the hit cache is empty, so this
    // reports "not hit" and control falls through.
    if (
      e.button === 0 &&
      schematic.handleSrvArrowClick(editor.uiState.mousePos.sch)
    ) {
      return;
    }

    // If a wire endpoint is highlighted (hover marker visible), a left-click
    // grabs that endpoint — this beats the normal hit-test, so the user
    // can't accidentally start a box-select or drag a nearby symbol when
    // the visual cue says "click here to stretch the wire". Mouse-up
    // commits the stretch via the standard drag-initiated path.
    if (e.button === 0) {
      const hovered = this.hoveredWireEndpoint;
      if (hovered) {
        return modes.StretchWireEndpoint.enter(
          schematic,
          hovered.wire,
          hovered.index
        );
      }
    }

    // Hit test, finding which element was clicked on.
    const whatd_we_hit = schematic.whatdWeHit(editor.uiState.mousePos);

    if (!whatd_we_hit) {
      // Right-click on empty space: show context menu
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        // Get the grid-snapped position for instance creation
        const gridPoint = editor.gridPointNearestToMouse();
        editor.emptySpaceContextMenu.show(
          e.pageX,
          e.pageY,
          gridPoint.x,
          gridPoint.y
        );
        return;
      }

      // Command-click (Cmd on Mac, Ctrl on Win/Linux): start panning.
      if (hasPlatformCmdModifier(e)) {
        editor.modeHandler = modes.Pan.start(editor);
        return;
      }

      // Hit "blank space". Start box-select (drag to select entities).
      // If the user clicks without dragging, this effectively deselects.
      return modes.BoxSelect.enter(editor, e.shiftKey);
    }

    // Right-click on an instance: update selection, then show context menu
    if (e.button === 2 && whatd_we_hit.entityKind === EntityKind.Instance) {
      e.preventDefault();
      e.stopPropagation(); // Prevent global handlers from closing menu immediately
      // Ensure the right-clicked item is in the selection
      if (!schematic.selection.has(whatd_we_hit)) {
        if (e.shiftKey) {
          schematic.addToSelection(whatd_we_hit);
        } else {
          schematic.select(whatd_we_hit);
        }
      }
      editor.schematicInstanceContextMenu.show(
        whatd_we_hit,
        e.pageX,
        e.pageY,
        schematic.selection.size
      );
      return;
    }

    // Right-click on a pin: select it and show pin context menu
    if (e.button === 2 && whatd_we_hit.entityKind === EntityKind.SchPin) {
      e.preventDefault();
      e.stopPropagation(); // Prevent global handlers from closing menu immediately
      schematic.select(whatd_we_hit);
      editor.pinContextMenu.show(whatd_we_hit, e.pageX, e.pageY);
      return;
    }

    // Right-click on a wire: select it and show wire context menu
    if (e.button === 2 && whatd_we_hit.entityKind === EntityKind.WireSegment) {
      e.preventDefault();
      e.stopPropagation(); // Prevent global handlers from closing menu immediately
      schematic.select(whatd_we_hit);
      editor.wireContextMenu.show(whatd_we_hit, e.pageX, e.pageY);
      return;
    }

    // Right-click on an annotation: select it, and show the text context menu
    // when it's a text note. (Schematic annotations are text-only today.)
    if (e.button === 2 && whatd_we_hit.entityKind === EntityKind.Annotation) {
      e.preventDefault();
      e.stopPropagation(); // Prevent global handlers from closing menu immediately
      schematic.select(whatd_we_hit);
      const prim = whatd_we_hit.prim;
      if (prim.entityKind === EntityKind.Text) {
        editor.textAnnotationContextMenu.show(prim, e.pageX, e.pageY);
      }
      return;
    }

    // Shift+click: toggle the entity in the multi-selection set.
    if (e.shiftKey) {
      return schematic.addToSelection(whatd_we_hit);
    }

    // Select the clicked-on entity and react based on its type.
    const { entityKind } = whatd_we_hit;
    const alreadySelected = schematic.selection.has(whatd_we_hit);
    // Check for the move-only modifier (Ctrl on Win/Linux, Cmd on Mac).
    // Default behavior drags connected wires along; holding Ctrl/Cmd moves the
    // entity only and leaves wires in place.
    const moveOnly = hasPlatformCmdModifier(e);
    switch (entityKind) {
      // "Movable" entities. Start moving them.
      //
      // One path for both the single-entity and multi-selection cases. There
      // used to be two: clicking a member of an existing multi-selection ran
      // `MoveSelection`, anything else ran `DragWithWires`, and the two
      // handled connected wires differently — the same gesture produced clean
      // orthogonal geometry or left diagonal wires behind depending only on
      // what happened to be selected beforehand.
      case EntityKind.SchPin:
      case EntityKind.Instance: {
        // Clicking a member of a multi-selection moves the whole selection;
        // anything else selects just this entity first.
        const inMultiSelection =
          alreadySelected && schematic.selection.size > 1;
        if (!inMultiSelection) schematic.select(whatd_we_hit);

        if (moveOnly && !inMultiSelection) {
          // Ctrl/Cmd on a single entity: it moves, wires stay in place.
          // `MoveXformable` also allows rotating and reflecting mid-move,
          // which the selection mode does not.
          modes.MoveXformable.enter(editor, whatd_we_hit);
        } else {
          modes.MoveSelection.enter(editor, !moveOnly, false, true);
        }
        return;
      }
      case EntityKind.WireSegment: {
        // Endpoint click → stretch from that endpoint.
        // Mid-segment click → select + drag the whole segment.
        // Both behaviors are LMB-only; right-click just selects.
        if (e.button === 0) {
          const endpointIndex = whatd_we_hit.hitTestEndpoint(
            editor.uiState.mousePos
          );
          if (endpointIndex !== null) {
            return modes.StretchWireEndpoint.enter(
              schematic,
              whatd_we_hit,
              endpointIndex
            );
          }
          // Single-segment slide. Multi-select drag and adjacent-stretch
          // are follow-up commits in this branch — see issue #2032.
          schematic.select(whatd_we_hit);
          modes.MoveWireSegment.enter(schematic, whatd_we_hit);
          return;
        }
        return schematic.select(whatd_we_hit);
      }
      case EntityKind.Annotation: {
        const { prim } = whatd_we_hit;
        // Test multi-selection membership by the underlying primitive (fresh
        // Annotation wrappers per hit-test defeat reference identity).
        const inMultiSelection =
          schematic.selection.size > 1 &&
          Array.from(schematic.selection).some(
            (s) => s instanceof Annotation && s.prim === prim
          );
        if (inMultiSelection) {
          // Member of a multi-selection: move the whole selection (like Instance).
          modes.MoveSelection.enter(editor, !moveOnly, false, true);
        } else {
          // Just this annotation; startMove handles graphics-group membership.
          schematic.select(whatd_we_hit);
          schematic.annotations.startMove(prim);
        }
        return;
      }
      // case EntityKind.PathVertex:
      // case EntityKind.RectCorner: {
      //   editor.modeHandler = Drag.start(editor, whatd_we_hit);
      //   // return schematic.select(whatd_we_hit); // FIXME! select/ highlight these too. Somehow.
      //   return;
      // }
      // case EntityKind.SymbolPin:
      // case EntityKind.RectBody:
      //   case EntityKind.TextLabel: {
      // FIXME: REMINDER THIS MODE DONT WORK RN!!!!
      //     editor.modeHandler = EditLabel.start(editor, whatd_we_hit);
      //     return schematic.select(whatd_we_hit);
      //   }
      // case EntityKind.EvalLabel:
      // case EntityKind.ExprLabel:
      // case EntityKind.Dot:
      //   break; // All these do nothing, at least for now.

      default:
        exhaust(entityKind);
    }
  };

  // Track which wire endpoint (if any) is currently under the cursor. The G
  // key reads this and enters keyboard-driven endpoint stretch when set; a
  // redraw is requested only on transitions so plain cursor motion stays cheap.
  override handleMouseMove = (): unknown => {
    const { editor, schematic } = this;
    const { mousePos } = editor.uiState;
    const prev = this.hoveredWireEndpoint;

    // SRV arrows take precedence, matching handleMouseDown's ordering: while an
    // arrow is under the cursor a click will select its violation, so showing
    // the endpoint grab marker underneath would promise an interaction the
    // click won't deliver. Redraw on the hover transition (the arrow itself
    // changes color) as well as on a change to the endpoint marker.
    const arrow = this.schematic.updateSrvArrowHover(mousePos.sch);
    if (arrow.hovered) {
      if (prev !== null) this.hoveredWireEndpoint = null;
      if (arrow.changed || prev !== null) editor.requestRedraw();
      return null;
    }

    // Pick the wire endpoint under the cursor. At a junction (two or more
    // wires sharing the same endpoint), the iteration order of `wires.all()`
    // would otherwise pick the same one every time — so a selected wire
    // wins, letting users disambiguate by first clicking the wire they
    // want to grab. Falls back to the first hit when nothing's selected.
    let next: { wire: WireSegment; index: 0 | 1 } | null = null;
    let fallback: { wire: WireSegment; index: 0 | 1 } | null = null;
    for (const wire of schematic.wires.all()) {
      const idx = wire.hitTestEndpoint(mousePos);
      if (idx === null) continue;
      if (schematic.selection.has(wire)) {
        next = { wire, index: idx };
        break;
      }
      fallback ??= { wire, index: idx };
    }
    next ??= fallback;

    const changed =
      (prev === null) !== (next === null) ||
      (prev !== null &&
        next !== null &&
        (prev.wire !== next.wire || prev.index !== next.index));
    if (changed) this.hoveredWireEndpoint = next;
    // `arrow.changed` here means the cursor just left an arrow, which recolors
    // it back to its resting color.
    if (changed || arrow.changed) {
      editor.requestRedraw();
    }
    return null;
  };

  // Overlay: a small ring drawn at the hovered wire endpoint so the user
  // can see what `G` will grab. Rendered as part of the idle-mode draw
  // layer so it sits on top of the wires without becoming part of any
  // entity's persistent geometry.
  override draw = (to: DrawTo): void => {
    const { editor } = this;
    const hovered = this.hoveredWireEndpoint;
    if (!hovered) return;
    const point =
      hovered.index === 0 ? hovered.wire.start() : hovered.wire.end();
    const scale = editor.canvas.worldTransforms.schToPage.scale;
    const radius = ENDPOINT_HOVER_RADIUS / scale;
    const diameter = radius * 2;
    const ellipse = graphics.Ellipse.fromPartial({
      filled: false,
      bbox: model.Bbox.fromSizeAndCenter(
        { width: diameter, height: diameter },
        point
      ),
    });
    to.ellipse(ellipse, {
      ...endpointHoverStyle,
      linewidth: endpointHoverStyle.linewidth / scale,
    });
  };

  // Handle keystrokes.
  override handleKey = (e: KeyboardEvent) => {
    const { editor, schematic } = this;
    const { Direction } = schdata;

    // Cmd/Ctrl+C and Cmd/Ctrl+V land here via editor.handleKey forwarding.
    // The plain-key switch below uses `e.key === "v"` for flip-vertical, so
    // we MUST short-circuit modifier+v before reaching that switch — without
    // this gate, Cmd+V would flip the selection (the original bug).
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
      if (e.key === "c") {
        // Copy/paste live on the stable Schematic content object (the
        // clipboard is an app-wide singleton), not on this transient handler.
        schematic.copySelected();
        return null;
      }
      if (e.key === "v") {
        schematic.paste();
        return null;
      }
    }

    // Pin direction shortcuts (I / O / X) when exactly one SchPin is
    // selected. Takes precedence over the global `Keys.i = AddInstance`
    // mapping in the switch below. Compare against the raw key strings
    // (= the values of Keys.i/o/x) — the lint rule rejects direct enum
    // equality outside of `switch/case`.
    if (schematic.selection.size === 1) {
      const [only] = schematic.selection;
      if (only.entityKind === EntityKind.SchPin) {
        const pinId = only.data.id;
        if (pinId !== undefined) {
          let newDir: schdata.PortDir | null = null;
          if (e.key === "i") newDir = schdata.PortDir.Input;
          else if (e.key === "o") newDir = schdata.PortDir.Output;
          else if (e.key === "x") newDir = schdata.PortDir.Inout;
          if (newDir !== null) {
            schematic.updatePinDirectionById(pinId, newDir);
            editor.showFeedback(`Pin direction: ${newDir}`);
            return;
          }
        }
      }
    }

    // All other UI states: check for "command" keystrokes.
    switch (e.key) {
      case Keys.Delete:
      case Keys.Backspace: {
        // Delete the selected entity
        const count = schematic.selection.size;
        const result = schematic.deleteSelected();
        if (result) editor.showItemFeedback("Deleted", count);
        return result;
      }
      // Mode-Changing Command Keys
      case Keys.i:
        return AddInstance.enter(schematic);
      case Keys.p:
        return AddSchPin.enter(schematic);
      case Keys.w:
        return editor.setMode(DrawWire.start(schematic));
      case Keys.e: // FIXME: why e? Why not?
        return schematic.annotations.startAddRect();

      // Rotation & refelection
      // Note these are versions of rotation & reflection which *are*
      // added to the undo/redo changelog.
      case Keys.r: {
        const count = schematic.selection.size;
        schematic.rotateSelected();
        if (count > 0) editor.showItemFeedback("Rotated", count);
        return;
      }
      case Keys.v: {
        const count = schematic.selection.size;
        schematic.flipSelected(Direction.Vert);
        if (count > 0) editor.showItemFeedback("Flipped", count, " vertically");
        return;
      }
      case Keys.h: {
        const count = schematic.selection.size;
        schematic.flipSelected(Direction.Horiz);
        if (count > 0)
          editor.showItemFeedback("Flipped", count, " horizontally");
        return;
      }

      // Box-select mode (keyboard entry: waits for first click)
      case Keys.b:
        return modes.BoxSelectWait.enter(editor);
      // Additive box-select mode (Shift+B: adds to existing selection)
      case Keys.B:
        return modes.BoxSelectWait.enter(editor, true);
      // Zoom-to-box mode (Z key: draw box, then zoom to fit it)
      case Keys.z:
        return modes.ZoomToBoxWait.enter(editor);
      // Grab / move selection
      case Keys.g:
        return this.handleGrabKey();
      // Cleanup wires (Shift+C): route wires with clean Manhattan paths
      case Keys.C: {
        const result = cleanupWires(schematic);
        if (result.changesApplied > 0) {
          editor.showItemFeedback(
            "Cleaned",
            result.networksProcessed,
            " wire routes"
          );
        }
        return;
      }
      default:
        // Note this *is not* an exhaustive check, on purpose.
        // There's lots of keys we don't care about!
        // Some day the logging should go away too.
        logger.log(`Key we dont use: '${e.key}'`);
    }
  };

  // Dispatch for the G (Grab) key. Split out of `handleKey` so the keyboard
  // switch stays a flat table and to keep wire-specific logic local.
  //
  // Priority order:
  //   1. Cursor over a wire endpoint → keyboard-driven endpoint stretch
  //      (no selection needed; hover is the source of truth for endpoints).
  //   2. Selection is exactly one wire → keyboard-driven sideways slide,
  //      which preserves adjacent perpendicular connections.
  //   3. Any other non-empty selection (instances, multiple wires, mixed)
  //      → rigid translation via MoveSelection. WireSegment translation is
  //      handled in `trySnapshot`; connections to non-selected entities
  //      break, consistent with how G already works for groups.
  //   4. Nothing selected and no hovered endpoint → no-op.
  private handleGrabKey = (): unknown => {
    const { editor, schematic } = this;
    const hovered = this.hoveredWireEndpoint;
    if (hovered) {
      return modes.StretchWireEndpoint.enterKeyboard(
        schematic,
        hovered.wire,
        hovered.index
      );
    }
    if (schematic.selection.size === 1) {
      const only = schematic.selection.values().next().value;
      if (only instanceof WireSegment) {
        return modes.MoveWireSegment.enterKeyboard(schematic, only);
      }
    }
    if (schematic.selection.size > 0) {
      return modes.MoveSelection.enter(editor);
    }
    return null;
  };
}

// Produce a collision-free name based on `base`, using an incremental integer
// suffix (the same strategy as duplicate). If `base` itself is free, it is
// returned unchanged (so pasting into a schematic that doesn't already have
// the name keeps it). Otherwise the trailing digits are stripped to form a
// prefix and the lowest free integer suffix is used: e.g. `M1` -> `M2`.
// Pure; shared between the duplicate, instance-paste, and SchPin-paste paths.
// Exported for unit-testing.
export function nextFreeName(base: string, existing: Set<string>): string {
  if (!existing.has(base)) return base;
  // Strip trailing digits to form the prefix; a purely-numeric base yields an
  // empty prefix, so e.g. "M12" -> "M1" and "12" -> "1".
  const prefix = base.replace(/\d+$/, "");
  for (let i = 1; ; i++) {
    const candidate = `${prefix}${i}`;
    if (!existing.has(candidate)) return candidate;
  }
}

// The PDK an instance's symbol belongs to, for the paste gate. Builtins are
// generic; PDK generators encode their PDK in the target path
// (["generators", <pdkId>, …]); anything else falls back to the source
// schematic's PDK. Exported for unit-testing.
export function instanceSymbolPdk(
  target: schdata.Instance["target"],
  sourcePdkId: string | undefined
): string | undefined {
  if (target.kind === "builtin") return undefined;
  if (
    target.kind === "hierpath" &&
    target.path.length >= 2 &&
    target.path[0] === "generators"
  ) {
    return target.path[1];
  }
  return sourcePdkId;
}

// Shallow-ish clone of schematic Instance data. Spreads the top level
// and clones the mutable nested fields we know about (xform, params
// array). `target` is a discriminated union of Ident shapes treated as
// immutable schema data elsewhere — keeping the shared reference is
// the existing convention. Computed inner fields (`conns`, `bbox`,
// `symbol`, …) ride along but the new Instance's `build()` rebuilds
// them from the schema fields.
function cloneInstanceData(data: schdata.Instance): schdata.Instance {
  return {
    ...data,
    xform: { ...data.xform },
    // Params is `Array<InstanceParam>` where InstanceParam can be either
    // an object (InstanceParamObject) or a tuple (InstanceParamTuple).
    // Object-spreading a tuple turns it into {0,1,…}, so we keep element
    // references shared — params are treated as immutable elsewhere.
    params: [...data.params],
  };
}

// Shallow-spread + nested-clone of a WireSegment's schema data.
function cloneWireData(
  data: schema.schematic.WireSegment
): schema.schematic.WireSegment {
  return {
    ...data,
    endpoints: [{ ...data.endpoints[0] }, { ...data.endpoints[1] }],
    style: { ...data.style },
    labels: data.labels.map((l) => ({ ...l })),
  };
}

export type GetSymbolError = {
  why: "timeout" | "invalid-builtin" | "invalid-symbol";
  message: string;
  data?: unknown;
};
